
'use client';

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/firebase';
import { signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup, signInWithRedirect, getRedirectResult } from 'firebase/auth';
import { FirebaseError } from 'firebase/app';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Logo } from '@/components/logo';
import { useToast } from '@/hooks/use-toast';
import { toErrorMessage } from '@/lib/error-utils';

function GoogleIcon(props: React.SVGProps<SVGSVGElement>) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 48 48"
        width="24px"
        height="24px"
        {...props}
      >
        <path
          fill="#FFC107"
          d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
        />
        <path
          fill="#FF3D00"
          d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
        />
        <path
          fill="#4CAF50"
          d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
        />
        <path
          fill="#1976D2"
          d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.574l6.19,5.238C39.99,34.556,44,29.865,44,24C44,22.659,43.862,21.35,43.611,20.083z"
        />
      </svg>
    );
  }

export function LoginForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState<'email' | 'google' | 'none'>('none');
  const [isProcessingRedirect, setIsProcessingRedirect] = useState(true);
  const auth = useAuth();
  const { toast } = useToast();

  const handleSignInError = useCallback((error: unknown) => {
    console.error(error);
    const description = toErrorMessage(error);
    
    let title = 'Sign In Failed';
    if (error instanceof FirebaseError) {
      switch (error.code) {
        case 'auth/user-not-found':
        case 'auth/wrong-password':
          title = 'Invalid Credentials';
          break;
        case 'auth/popup-closed-by-user':
          title = 'Sign-in Cancelled';
          break;
      }
    }

    toast({
        variant: 'destructive',
        title: title,
        description,
    });
  }, [toast]);

  // Handle redirect result on component mount
  useEffect(() => {
    const handleRedirectResult = async () => {
      try {
        // The onAuthStateChanged listener in FirebaseProvider will handle the user object.
        // We just need to process the redirect result here to complete the flow.
        await getRedirectResult(auth);
      } catch (error) {
        if (error instanceof FirebaseError && error.code !== 'auth/operation-not-supported-in-this-environment') {
            handleSignInError(error);
        }
      } finally {
        setIsProcessingRedirect(false);
      }
    };

    handleRedirectResult();
  }, [auth, handleSignInError]);

  const handleGoogleSignIn = async () => {
    setIsLoading('google');
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });

    try {
      await signInWithPopup(auth, provider);
      // On success, onAuthStateChanged in provider will trigger session creation.
    } catch (error) {
      if (
        error instanceof FirebaseError &&
        (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/popup-blocked')
      ) {
        console.log('Popup failed, falling back to redirect...');
        await signInWithRedirect(auth, provider);
      } else {
        handleSignInError(error);
        setIsLoading('none');
      }
    }
    // No need to set isLoading to 'none' on success, as page will redirect or state will change.
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading('email');
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // On success, onAuthStateChanged in provider will trigger session creation.
    } catch (error) {
      handleSignInError(error);
    } finally {
      setIsLoading('none');
    }
  };

  if (isProcessingRedirect) {
    return <Loader2 className="animate-spin h-8 w-8 text-primary" />;
  }

  return (
    <div className="w-full max-w-md space-y-6">
      <div className="flex justify-center">
        <Logo />
      </div>
      <Card className="w-full border-solid shadow-sm">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-semibold">Welcome Back</CardTitle>
          <CardDescription>Sign in to continue to Whisk</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Button
              variant="outline"
              className="w-full"
              type="button"
              onClick={handleGoogleSignIn}
              disabled={isLoading !== 'none'}
            >
              {isLoading === 'google' ? (
                <Loader2 className="animate-spin" />
              ) : (
                <>
                  <GoogleIcon />
                  <span className="ml-2">Sign in with Google</span>
                </>
              )}
            </Button>
            <div className="flex items-center space-x-2">
              <Separator className="flex-1" />
              <span className="text-xs text-muted-foreground">OR</span>
              <Separator className="flex-1" />
            </div>
            <form onSubmit={handleEmailSignIn} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="m@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  disabled={isLoading !== 'none'}
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link
                    href="#"
                    className="text-sm font-medium text-primary hover:underline"
                  >
                    Forgot password?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  disabled={isLoading !== 'none'}
                />
              </div>
              <Button
                className="w-full font-bold"
                type="submit"
                disabled={isLoading !== 'none'}
              >
                {isLoading === 'email' ? <Loader2 className="animate-spin" /> : 'Sign In' }
              </Button>
            </form>
          </div>
          <p className="mt-4 text-center text-sm text-muted-foreground">
            Don&apos;t have an account?{' '}
            <Link
              href="/signup"
              className="font-medium text-primary hover:underline"
            >
              Sign up
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
